import React, { useContext } from "react";
import { motion, useAnimation } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { DollarSign, ShoppingCart, CreditCard, Briefcase } from "lucide-react";
import { CanvasContext } from "../../contexts/CanvasContext";
import "./SoftwarePricing.css";

const SoftwarePricing = () => {
  const { isDarkTheme } = useContext(CanvasContext);
  const controls = useAnimation();
  const [ref, inView] = useInView({
    triggerOnce: false,
    threshold: 0.1,
  });

  React.useEffect(() => {
    if (inView) {
      controls.start("visible");
    } else {
      controls.start("hidden");
    }
  }, [controls, inView]);

  const headerVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  const planVariants = {
    hidden: { opacity: 0, y: 100 },
    visible: (custom) => ({
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, delay: custom * 0.1 },
    }),
  };

  const pricingData = [
    {
      title: "Free",
      price: 0,
      icon: (
        <ShoppingCart
          className={`nexgen-pricing__plan-icon ${
            isDarkTheme ? "dark-theme" : ""
          }`}
        />
      ),
      features: [
        "Single User",
        "Basic Access to Component Library",
        "Limited Customization Options",
        "No Updates",
      ],
    },
    {
      title: "Basic",
      price: 19,
      icon: (
        <DollarSign
          className={`nexgen-pricing__plan-icon ${
            isDarkTheme ? "dark-theme" : ""
          }`}
        />
      ),
      features: [
        "Single User",
        "Standard Access to Component Library",
        "Basic Customization Options",
        "Monthly Updates",
      ],
    },
    {
      title: "Standard",
      price: 39,
      icon: (
        <CreditCard
          className={`nexgen-pricing__plan-icon ${
            isDarkTheme ? "dark-theme" : ""
          }`}
        />
      ),
      features: [
        "Single User",
        "Extended Access to Component Library",
        "Advanced Customization Options",
        "Bi-Weekly Updates",
        "Priority Support",
      ],
    },
    {
      title: "Pro",
      price: 99,
      icon: (
        <Briefcase
          className={`nexgen-pricing__plan-icon ${
            isDarkTheme ? "dark-theme" : ""
          }`}
        />
      ),
      features: [
        "Single User",
        "Full Access to Component Library",
        "Unlimited Customization Options",
        "Weekly Updates",
        "Premium Support",
        "Dedicated Account Manager",
      ],
    },
  ];

  return (
    <div
      className={`nexgen-pricing ${isDarkTheme ? "dark-theme" : ""}`}
      ref={ref}
    >
      <motion.div
        className="nexgen-pricing__header"
        initial="hidden"
        animate={controls}
        variants={headerVariants}
      >
        <h2
          className={`nexgen-pricing__title ${isDarkTheme ? "dark-theme" : ""}`}
        >
          PRICING
        </h2>
        <h3
          className={`nexgen-pricing__subtitle ${
            isDarkTheme ? "dark-theme" : ""
          }`}
        >
          Our Pricing
        </h3>
        <p
          className={`nexgen-pricing__description ${
            isDarkTheme ? "dark-theme" : ""
          }`}
        >
          At NexGen Sites, we offer flexible pricing plans to suit different
          needs. Each plan provides access to a variety of features designed to
          make website creation simple and efficient. Choose the one that best
          fits your requirements.
        </p>
      </motion.div>
      <div
        className={`nexgen-pricing__plans ${isDarkTheme ? "dark-theme" : ""}`}
      >
        {pricingData.map((plan, index) => (
          <motion.div
            key={index}
            className={`nexgen-pricing__plan ${
              plan.title.toLowerCase() === "standard"
                ? "nexgen-pricing__plan--highlighted"
                : ""
            }`}
            custom={index}
            initial="hidden"
            animate={controls}
            variants={planVariants}
          >
            <div className="nexgen-pricing__plan-header">
              {plan.icon}
              <h4 className="nexgen-pricing__plan-title">{plan.title}</h4>
              <div className="nexgen-pricing__plan-price">
                <span className="nexgen-pricing__plan-price-currency">$</span>
                <span className="nexgen-pricing__plan-price-amount">
                  {plan.price}
                </span>
              </div>
              <p className="nexgen-pricing__plan-billing">Per Project</p>
            </div>
            <ul className="nexgen-pricing__plan-features">
              {plan.features.map((feature, featureIndex) => (
                <li key={featureIndex} className="nexgen-pricing__plan-feature">
                  <span className="nexgen-pricing__plan-feature-icon">✓</span>
                  {feature}
                </li>
              ))}
            </ul>
            <button
              className={`nexgen-pricing__plan-cta ${
                isDarkTheme ? "dark-theme" : ""
              }`}
            >
              Start Now
            </button>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default SoftwarePricing;
